import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import { GetLogo, signUp } from "../../apis/api/AuthApi";
import { sendOTP } from "../../apis/api/OTPApi";
import { countryCode, CountryList } from "../../apis/api/CountryList";
import { MembershipPlanList } from "../../apis/api/MembershipApi";
import Lodderr from "../../components/Lodder/Lodder";
import OTPModal from "../../components/OTPModal/OTPModal";
import "./SignUp.css";

/* shared react-select styles */
const rs = (err) => ({
  control:(b,s)=>({ ...b, borderRadius:"10px", minHeight:"40px",
    border:`1.5px solid ${err?"#ef4444":s.isFocused?"#586AD9":"#e5e7eb"}`,
    boxShadow:s.isFocused?"0 0 0 3px rgba(88,106,217,.10)":"none",
    background:"#fafafa", fontSize:".85rem",
    "&:hover":{ borderColor:err?"#ef4444":"#586AD9" } }),
  option:(b,s)=>({ ...b, fontSize:".85rem",
    background:s.isSelected?"#586AD9":s.isFocused?"#eef0fb":"#fff",
    color:s.isSelected?"#fff":"#0f172a" }),
  menuPortal:(b)=>({ ...b, zIndex:9999 }),
  menu:(b)=>({ ...b, zIndex:9999, borderRadius:"10px" }),
});

const SignUp = () => {
  const { register, handleSubmit, setValue, watch, control, formState: { errors } } = useForm();
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const [membershipPlans, setMembershipPlans] = useState([]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);

  const companyEmail = watch("email");

  const { country, countryCode: ccRaw } = useSelector(s => s.countrySlice);
  // ccRaw could be the full response object or array — extract safely
  const ccList = Array.isArray(ccRaw) ? ccRaw
    : Array.isArray(ccRaw?.data?.data) ? ccRaw.data.data
    : Array.isArray(ccRaw?.data)       ? ccRaw.data
    : [];
  const { MembershiplanList }    = useSelector(s => s.MembershipSlice);
  const { companyLogo, loading } = useSelector(s => s.AuthSlice);

  useEffect(() => { dispatch(GetLogo()); }, [dispatch]);

  useEffect(() => {
    dispatch(CountryList());
    dispatch(countryCode());
    dispatch(MembershipPlanList());
  }, [dispatch]);

  useEffect(() => {
    if (MembershiplanList?.data) {
      const plans = MembershiplanList.data.data;
      setMembershipPlans(plans);
      // Auto-select Membership Plan ID 14
      const standard = plans?.find(p => p.value?.toString().startsWith('14:'))
        || plans?.[0];
      if (standard) setValue("membershipPlan", standard.value);
    }
  }, [MembershiplanList, setValue]);

  // Pre-fill Select dropdowns once options are loaded
  useEffect(() => {
    if (Array.isArray(country) && country.length > 0) {
      const uae = country.find(c => /united arab emirates|uae/i.test(c.label)) || country[0];
      setValue("country", uae?.value);
      setValue("Membercountry", uae?.value);
    }
  }, [country, setValue]);

  useEffect(() => {
    if (ccList.length > 0) {
      const uaeCode = ccList.find(c => c.value === "+971") || ccList[0];
      setValue("contact_country_code", uaeCode);
      setValue("telephone_country_code", uaeCode);
    }
  }, [ccList.length, setValue]);

  // Handle Send OTP
  const handleSendOTP = async (email) => {
    setSendingOTP(true);
    try {
      const res = await dispatch(sendOTP(email)).unwrap();
      
      if (res.status === "success") {
        toast.success("OTP sent to your email!");
        setIsOTPModalOpen(true);
        return true;
      } else if (res.status === "email_exists") {
        toast.error(res.message || "Email already registered");
        return false;
      } else {
        toast.error(res.message || "Failed to send OTP");
        return false;
      }
    } catch (error) {
      toast.error(error.message || "Failed to send OTP");
      return false;
    } finally {
      setSendingOTP(false);
    }
  };

  const handleOTPVerified = async () => {
    setIsOTPModalOpen(false);
    if (pendingFormData) {
      await submitRegistration(pendingFormData);
    }
  };

  // Reset verification if email changes
  useEffect(() => {
    setPendingFormData(null);
  }, [companyEmail]);

  const formSkipKeys = [
    "member_birthday",
    "companyEstablishmentDate",
    "membershipPlan",
    "contact_country_code",
    "telephone_country_code",
  ];

  // Submit registration after OTP verification
  const submitRegistration = async (data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => {
      if (!formSkipKeys.includes(k)) fd.append(k, v);
    });

    // Append the auto-selected membership plan
    if (data.membershipPlan) fd.append("membershipPlan", data.membershipPlan);

    if (data.contact_country_code?.value != null) {
      fd.append("contact", data.contact_country_code.value);
    }
    if (data.telephone_country_code?.value != null) {
      fd.append("telephone", data.telephone_country_code.value);
    }

    const toTs = (d) => (d ? new Date(d).getTime() : "");
    if (data.member_birthday) fd.append("member_birthday", toTs(data.member_birthday));
    if (data.companyEstablishmentDate) {
      fd.append("companyEstablishmentDate", toTs(data.companyEstablishmentDate));
    }

    try {
      const res = await dispatch(signUp(fd));
      if (signUp.rejected.match(res))  { toast.error(res.payload?.message || "Something went wrong."); return; }
      if (signUp.fulfilled.match(res)) {
        const p = res.payload;
        if      (p?.status === "success")          { toast.success("Your application has been submitted! We'll review it and get back to you shortly."); navigate("/thankyou"); }
        else if (p?.status === "email_exists")     toast.error(p.message || "Email already exists.");
        else if (p?.status === "email_not_verified") toast.error(p.message || "Please verify your email first.");
        else if (p?.status === "invalid_referral") toast.error(p.message || "Invalid referral code.");
        else                                       toast.error(p.message || "Unknown error.");
      }
    } catch { toast.error("Unexpected error occurred."); }
  };

  const onSubmit = async (data) => {
    // Validate email
    if (!data.email) {
      toast.error("Please enter company email");
      return;
    }

    // Store form data and send OTP
    setPendingFormData(data);
    
    // Send OTP
    const otpSent = await handleSendOTP(data.email);
    if (!otpSent) {
      // If OTP sending failed, clear pending data
      setPendingFormData(null);
    }
  };

  return (
    <>
      {loading && <Lodderr />}
      <div className="su-wrap">

        {/* ── LEFT ── */}
        <div className="su-left">
          <img src={`${process.env.PUBLIC_URL}/images/logistic.jpg`}
            className="su-left-img" alt="Logistics"
            onError={e => { e.target.style.display="none"; }} />
          <div className="su-left-overlay" />
          <motion.div className="su-left-content"
            initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }}
            transition={{ duration:.6, ease:[.22,.68,0,1.2] }}>
            <span className="su-left-badge">
              <i className="bx bx-world"></i> Global Logistics Network
            </span>
            <h1 className="su-left-title">Join the network.<br />Grow your reach.</h1>
            <p className="su-left-sub">
              Create your company profile, connect with members, manage freight
              requests and invoices — all in one modern workspace.
            </p>
            <div className="su-steps">
              {["Member information","Company details","Submit for approval"].map((s,i)=>(
                <div key={i} className="su-step">
                  <span className="su-step-num">{i+1}</span>{s}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── RIGHT ── */}
        <motion.div className="su-right"
          initial={{ opacity:0, x:30 }} animate={{ opacity:1, x:0 }}
          transition={{ duration:.5, ease:[.22,.68,0,1.2] }}>

          {/* Logo */}
          <div className="su-logo-wrap">
            <img
              src={companyLogo ? companyLogo.replace("/thumb","") : `${process.env.PUBLIC_URL}/company-logo-design.jpg`}
              className="su-logo-img" alt="Logo"
              onError={e => { e.target.onerror=null; e.target.src=`${process.env.PUBLIC_URL}/company-logo-design.jpg`; }} />
          </div>

          <h1 className="su-heading">Create your account</h1>
          <p className="su-sub-text">Fill in the details below to register as a member.</p>

          {/* Membership Fee Note */}
          <div className="su-fee-note">
            <div className="su-fee-note-header">
              <i className="bx bx-info-circle"></i>
              <span>Membership Fee Information</span>
            </div>
            <p><strong>Annual Dues (raised in January each year):</strong></p>
            <ul>
              <li><strong>$2,125</strong> — Annual Dues (includes M2M Liability Fund and $725 AGM Attendance Fee for ONE attendee - non refundable)</li>
              <li><strong>$200</strong> — Branch Office Fee (for those with additional offices)</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>

            {/* ── MEMBER INFO ── */}
            <div className="su-section">
              <p className="su-section-title"><i className="bx bx-user"></i> Member Information</p>
              <div className="su-grid-2">
                <div className="su-field">
                  <label className="su-label">First Name <span>*</span></label>
                  <input className={`su-input ${errors.member_firstName?"error":""}`}
                    placeholder="First name"
                    {...register("member_firstName",{ required:"Required", pattern:{ value:/^[A-Za-z]+$/, message:"Letters only" } })} />
                  {errors.member_firstName && <span className="su-err">{errors.member_firstName.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Last Name <span>*</span></label>
                  <input className={`su-input ${errors.member_lastName?"error":""}`}
                    placeholder="Last name"
                    {...register("member_lastName",{ required:"Required", pattern:{ value:/^[A-Za-z]+$/, message:"Letters only" } })} />
                  {errors.member_lastName && <span className="su-err">{errors.member_lastName.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Job Title <span>*</span></label>
                  <input className={`su-input ${errors.member_jobTitle?"error":""}`}
                    placeholder="e.g. Logistics Manager"
                    {...register("member_jobTitle",{ required:"Required", minLength:{ value:2, message:"Min 2 chars" } })} />
                  {errors.member_jobTitle && <span className="su-err">{errors.member_jobTitle.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Member Country <span>*</span></label>
                  <Select options={country} placeholder="Select country"
                    styles={rs(!!errors.Membercountry)}
                    menuPortalTarget={document.body} menuPosition="fixed"
                    onChange={o => setValue("Membercountry", o?.value)} />
                  {errors.Membercountry && <span className="su-err">{errors.Membercountry.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Email Address <span>*</span></label>
                  <input type="email" className={`su-input ${errors.member_email?"error":""}`}
                    placeholder="you@company.com"
                    {...register("member_email",{ required:"Required", pattern:{ value:/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message:"Invalid email" } })} />
                  {errors.member_email && <span className="su-err">{errors.member_email.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Contact Number <span>*</span></label>
                  <div className="su-phone-row">
                    <Controller
                      name="contact_country_code"
                      control={control}
                      rules={{ required: "Country code is required" }}
                      render={({ field, fieldState }) => (
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          options={ccList}
                          placeholder="Code"
                          getOptionLabel={(e) => e.value}
                          styles={rs(!!fieldState.error)}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      )}
                    />
                    <input
                      type="tel"
                      className={`su-input ${errors.member_contactNumber ? "error" : ""}`}
                      placeholder="Contact number"
                      style={{ flex: 1 }}
                      {...register("member_contactNumber", {
                        required: "Required",
                        pattern: { value: /^[0-9]{7,15}$/, message: "Invalid number" },
                      })}
                    />
                  </div>
                  {errors.contact_country_code && (
                    <span className="su-err">{errors.contact_country_code.message}</span>
                  )}
                  {errors.member_contactNumber && (
                    <span className="su-err">{errors.member_contactNumber.message}</span>
                  )}
                </div>
                <div className="su-field">
                  <label className="su-label">Birthday <span>*</span></label>
                  <Controller
                    name="member_birthday"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        className={`su-input ${fieldState.error ? "error" : ""}`}
                        placeholderText="Select birthdate"
                        selected={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        dateFormat="MM/dd/yyyy"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={100}
                        showMonthDropdown
                      />
                    )}
                  />
                  {errors.member_birthday && (
                    <span className="su-err">{errors.member_birthday.message}</span>
                  )}
                </div>
              </div>
            </div>

            {/* ── COMPANY INFO ── */}
            <div className="su-section">
              <p className="su-section-title"><i className="bx bx-building"></i> Company Information</p>
              <div className="su-grid-2">
                <div className="su-field">
                  <label className="su-label">Company Name <span>*</span></label>
                  <input className={`su-input ${errors.companyName?"error":""}`}
                    placeholder="Your company name"
                    {...register("companyName",{ required:"Required" })} />
                  {errors.companyName && <span className="su-err">{errors.companyName.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Company Logo <span>*</span></label>
                  <Controller name="companyLogo" control={control}
                    rules={{ required:"Logo is required" }}
                    render={({ field }) => (
                      <>
                        <input type="file" accept="image/*"
                          className={`su-input ${errors.companyLogo?"error":""}`}
                          style={{ padding:".4rem .85rem" }}
                          onChange={e => {
                            const f = e.target.files[0];
                            field.onChange(f);
                            setPreviewUrl(URL.createObjectURL(f));
                          }} />
                        {previewUrl && <img src={previewUrl} className="su-logo-preview" alt="preview" />}
                      </>
                    )} />
                  {errors.companyLogo && <span className="su-err">{errors.companyLogo.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Telephone <span>*</span></label>
                  <div className="su-phone-row">
                    <Controller
                      name="telephone_country_code"
                      control={control}
                      rules={{ required: "Country code is required" }}
                      render={({ field, fieldState }) => (
                        <Select
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          options={ccList}
                          placeholder="Code"
                          getOptionLabel={(e) => e.value}
                          styles={rs(!!fieldState.error)}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      )}
                    />
                    <input
                      type="tel"
                      className={`su-input ${errors.company_telephone ? "error" : ""}`}
                      placeholder="Telephone"
                      style={{ flex: 1 }}
                      {...register("company_telephone", { required: "Required" })}
                    />
                  </div>
                  {errors.telephone_country_code && (
                    <span className="su-err">{errors.telephone_country_code.message}</span>
                  )}
                  {errors.company_telephone && (
                    <span className="su-err">{errors.company_telephone.message}</span>
                  )}
                </div>
                <div className="su-field">
                  <label className="su-label">No. of Employees <span>*</span></label>
                  <input type="number" className={`su-input ${errors.numEmployees?"error":""}`}
                    placeholder="e.g. 50"
                    {...register("numEmployees",{ required:"Required" })} />
                  {errors.numEmployees && <span className="su-err">{errors.numEmployees.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Country <span>*</span></label>
                  <Select options={country} placeholder="Select country"
                    styles={rs(!!errors.country)}
                    menuPortalTarget={document.body} menuPosition="fixed"
                    onChange={o => setValue("country", o?.value)} />
                  {errors.country && <span className="su-err">{errors.country.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">City <span>*</span></label>
                  <input className={`su-input ${errors.city?"error":""}`}
                    placeholder="City"
                    {...register("city",{ required:"Required" })} />
                  {errors.city && <span className="su-err">{errors.city.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Company Email <span>*</span></label>
                  <input type="email" className={`su-input ${errors.email?"error":""}`}
                    placeholder="company@email.com"
                    {...register("email",{ required:"Required", pattern:{ value:/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message:"Invalid email" } })} />
                  {errors.email && <span className="su-err">{errors.email.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">Establishment Date <span>*</span></label>
                  <Controller name="companyEstablishmentDate" control={control}
                    rules={{ required:"Required" }}
                    render={({ field }) => (
                      <DatePicker className="su-input" placeholderText="Select date"
                        selected={field.value} onChange={d => field.onChange(d)}
                        dateFormat="MM/dd/yyyy" showYearDropdown scrollableYearDropdown
                        yearDropdownItemNumber={100} showMonthDropdown />
                    )} />
                  {errors.companyEstablishmentDate && <span className="su-err">{errors.companyEstablishmentDate.message}</span>}
                </div>
              </div>

              <div className="su-grid-1" style={{ marginTop:".85rem" }}>
                <div className="su-field">
                  <label className="su-label">Company Address <span>*</span></label>
                  <textarea className={`su-input ${errors.companyAddress?"error":""}`} rows={3}
                    placeholder="Full company address"
                    {...register("companyAddress",{ required:"Required" })} />
                  {errors.companyAddress && <span className="su-err">{errors.companyAddress.message}</span>}
                </div>
                <div className="su-field">
                  <label className="su-label">About Your Company <span>*</span></label>
                  <textarea className={`su-input ${errors.aboutCompany?"error":""}`} rows={3}
                    placeholder="Brief description of your company"
                    {...register("aboutCompany",{ required:"Required" })} />
                  {errors.aboutCompany && <span className="su-err">{errors.aboutCompany.message}</span>}
                </div>
              </div>
            </div>

            <button type="submit" className="su-btn" disabled={loading || sendingOTP}>
              <i className="bx bxs-check-circle"></i>
              {sendingOTP ? "Sending OTP…" : loading ? "Submitting…" : "Create Account"}
            </button>

            <div className="su-foot">
              <span>Already have an account?</span>
              <Link to="/" className="su-link">Sign in</Link>
            </div>
          </form>
        </motion.div>

        {/* OTP Modal */}
        <OTPModal
          isOpen={isOTPModalOpen}
          onClose={() => setIsOTPModalOpen(false)}
          email={companyEmail}
          onVerified={handleOTPVerified}
        />

      </div>
    </>
  );
};

export default SignUp;
